import API from 'services/api';
import { isEmpty } from 'lodash-es';
import {
  CARD_TOKENIZATION_CARD_MANAGEMENT,
  CARD_TOKENIZATION_ERROR,
  APP_ROUTE_NOT_FOUND
} from 'routes/paths';
import { displaySpinner, hideSpinner } from 'redux/actions/global';
import { disableCardAbstraction } from 'redux/actions/card';
import {
  getCustomerPaymentMethods,
  setDefaultCardStart,
  setDefaultCardSuccess,
  setDefaultCardEnd,
  setDefaultCardFail
} from 'redux/actions/customerReduxActions';
import { onChangeCardForm } from 'redux/actions/cardForm';
import * as CONSTS from 'redux/constants';
import history from 'shared/services/history';
import { sendGetPageFailMessage } from 'services/iframeService';
import showErrorAlert, { parseError } from 'services/errorHandler';
import { redirectToError, getRouteWithToken, trackSaveCard } from 'utilities';
import { getClientDetails } from 'utilities/global';
import {
  STATUSES,
  HOSTED_PAGE_TYPES,
  CHECKBOX_TYPES,
  CATEGORIES,
  GA_LABEL
} from 'consts';
import { CARD_ABSTRACION_ERROR } from 'redux/constants';

export const getCardTokenizationPageStart = () => ({
  type: CONSTS.GET_CARD_TOKENIZATION_PAGE_START
});

export const getCardTokenizationPageSuccess = data => ({
  type: CONSTS.GET_CARD_TOKENIZATION_PAGE_SUCCESS,
  payload: { data }
});

export const getCardTokenizationPageFail = (error, data) => ({
  type: CONSTS.GET_CARD_TOKENIZATION_PAGE_FAIL,
  payload: { data, error }
});

export const tokenizeCardStart = () => ({
  type: CONSTS.TOKENIZE_CARD_START
});

export const tokenizeCardSuccess = data => ({
  type: CONSTS.TOKENIZE_CARD_SUCCESS,
  payload: { data }
});

export const tokenizeCardFail = error => ({
  type: CONSTS.TOKENIZE_CARD_FAIL,
  payload: { error }
});

export const updateCardTokenizationPage = updateObject => ({
  type: CONSTS.UPDATE_CARD_TOKENIZATION_PAGE,
  payload: updateObject
});

export const notifyCardTokenizationFail = token => ({
  type: CONSTS.NOTIFY_CARD_TOKENIZATION_FAIL,
  payload: { token }
});

export const setCardPayment = ({ token, status, next_action, card_payment_token }) => ({
  type: CONSTS.SET_CARD_PAYMENT,
  payload: { token, status, next_action, card_payment_token }
});

export const getCardTokenizationPage =
  (token, onManagementScreen = false) =>
  async (dispatch, getState) => {
    try {
      const { isSdk } = getState().global;
      const shouldFetchSavedCards = !isSdk && !onManagementScreen;
      dispatch(displaySpinner());

      const cardPageData = await fetchCardTokenizationPage(token, dispatch);
      const isPageInitialized = !isEmpty(cardPageData);
      if (shouldFetchSavedCards && isPageInitialized) {
        await fetchSavedCards({
          pageToken: token,
          customerToken: cardPageData?.customer,
          dispatch
        });
      }
    } catch (error) {
      const { status, data } = parseError(error);
      dispatch(getCardTokenizationPageFail(status, data));
      redirectToError({ error, tokenization: true, url: CARD_TOKENIZATION_ERROR });
    } finally {
      dispatch(hideSpinner({ meta: { pageType: HOSTED_PAGE_TYPES.cardTokenization } }));
    }
  };

export const getCardTokenizationPageByPayment =
  (token, stay, showLoader = true) =>
  async dispatch => {
    const handleGetPageError = (error, url = '') => {
      const { status, data } = parseError(error);
      dispatch(getCardTokenizationPageFail(status, data));
      redirectToError({ error, tokenization: true, url });
    };

    if (!token) {
      return history.push(APP_ROUTE_NOT_FOUND);
    }

    dispatch(getCardTokenizationPageStart());
    if (showLoader) dispatch(displaySpinner());

    try {
      let cardPage;
      try {
        cardPage = await API.get(`hosted/collect/card/payment/${token}`);
      } catch (error) {
        return handleGetPageError(error, APP_ROUTE_NOT_FOUND);
      }

      const { data: cardPageData } = cardPage.data;
      const { card_token, page, payment_status, next_action, card_payment_token } =
        cardPageData;

      dispatch(getCardTokenizationPageSuccess(page));

      dispatch(
        setCardPayment({
          token: card_token,
          status: payment_status,
          next_action,
          card_payment_token
        })
      );

      if (page?.status === STATUSES.done && !stay) {
        history.push(`${CARD_TOKENIZATION_CARD_MANAGEMENT}?token=${token}`);
      }
    } catch (error) {
      handleGetPageError(error, CARD_TOKENIZATION_ERROR);
    } finally {
      if (showLoader)
        dispatch(hideSpinner({ meta: { pageType: HOSTED_PAGE_TYPES.cardTokenization } }));
    }
  };

export const tokenizeCard =
  ({
    pageToken,
    customerToken,
    successCallback,
    parsedData: {
      paymentMethodType,
      fields,
      pmOptions,
      billingAddress,
      setCardAsDefault,
      customerFields = null
    }
  }) =>
  async (dispatch, getState) => {
    try {
      const { isPreview, sdkParams } = getState().global;
      const { waitOnPaymentRedirect } = sdkParams || {};

      if (isPreview) return;

      if (isEmpty(paymentMethodType)) {
        dispatch(disableCardAbstraction());

        throw CARD_ABSTRACION_ERROR;
      }
      dispatch(tokenizeCardStart());
      const { sessionId, isMobile } = getState().global;
      const apiData = {
        payment_method_type: paymentMethodType?.type,
        required_fields: fields,
        payment_method_options: isEmpty(pmOptions) ? null : pmOptions,
        customer: customerToken,
        client_details: getClientDetails(sessionId, isMobile),
        set_default_payment_method: setCardAsDefault,
        address: billingAddress,
        customer_required_fields: customerFields,
        sdk_wait_on_payment_redirect: waitOnPaymentRedirect
      };

      const response = await API.post(
        `hosted/collect/card/${pageToken}/payment_method`,
        apiData
      );
      const {
        data: { payment, complete_url: completeUrl }
      } = response.data;
      dispatch(tokenizeCardSuccess(payment));
      dispatch(
        updateCardTokenizationPage({ status: STATUSES.done, complete_url: completeUrl })
      );
      if (payment.redirect_url) {
        window.location.href = payment.redirect_url;
        return;
      }
      if (successCallback) {
        successCallback();
      }
    } catch (error) {
      showErrorAlert({ error });
      const { status } = parseError(error);
      trackSaveCard(GA_LABEL.FAILURE);
      dispatch(tokenizeCardFail(status));
    }
  };

export const setDefaultCard =
  ({ cardId, pageToken }) =>
  async dispatch => {
    try {
      dispatch(setDefaultCardStart());
      await API.put(`/hosted/collect/card/${pageToken}/payment_method`, { card: cardId });
      dispatch(setDefaultCardSuccess(cardId));
    } catch (error) {
      showErrorAlert({ error });
      dispatch(setDefaultCardFail(cardId));
    } finally {
      dispatch(setDefaultCardEnd());
    }
  };

async function fetchCardTokenizationPage(token, dispatch) {
  if (!token) {
    sendGetPageFailMessage(HOSTED_PAGE_TYPES.cardTokenization)();
    return redirectToError({ url: APP_ROUTE_NOT_FOUND });
  }

  dispatch(getCardTokenizationPageStart());

  let cardPage;
  try {
    cardPage = await API.get(`hosted/collect/card/${token}`);
  } catch (error) {
    const { status, data } = parseError(error);
    dispatch(getCardTokenizationPageFail(status, data));
    return redirectToError({ error, tokenization: true, url: APP_ROUTE_NOT_FOUND });
  }

  const { data: cardPageData } = cardPage.data;
  dispatch(getCardTokenizationPageSuccess(cardPageData));
  dispatch(
    onChangeCardForm({
      fieldPath: CHECKBOX_TYPES.USE_MAIN_ADDRESS,
      value: true,
      isCheckbox: true
    })
  );
  return cardPageData;
}

async function fetchSavedCards({ pageToken, customerToken, dispatch }) {
  const { data, defaultCard } = await dispatch(
    // @ts-ignore
    getCustomerPaymentMethods({
      pageToken,
      customerToken,
      category: CATEGORIES.card
    })
  );

  if (defaultCard) {
    dispatch(setDefaultCardSuccess(defaultCard));
  }

  if (data.length) {
    return history.push(getRouteWithToken(CARD_TOKENIZATION_CARD_MANAGEMENT, pageToken), {
      shouldRefreshList: false
    });
  }
}
